// iter114 — Admin Léa : gestion des catégories + sous-catégories (prix + durée atelier)
// Affichage : liste de cards par catégorie, édition inline via modal, CRUD complet.
// Le champ duree_minutes sur chaque sous-catégorie alimente le calcul de capacité agenda.

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, X, ChevronDown, ChevronUp, Sparkles, AlertCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_CHOICES = [
  "Wrench", "Disc", "Car", "ClipboardCheck", "HelpCircle",
  "Snowflake", "Sparkles", "Settings", "Zap", "Battery", "Fuel",
];

function authHeaders() {
  const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtPrice(n) {
  if (n == null) return "—";
  return Number(n).toFixed(2) + " CHF";
}

function newSubcategory() {
  return {
    id: null,
    label: "",
    prix: 0,
    duree_minutes: 60,
    ordre: 1,
    actif: true,
  };
}

function emptyCategory() {
  return {
    id: null,
    label: "",
    icon: "HelpCircle",
    subtitle: "",
    ordre: 10,
    actif: true,
    chip_text: "",
    is_other: false,
    propose_checkup_upsell: false,
    info_text: "",
    duree_minutes_defaut: 60,
    subcategories: [],
  };
}

export default function WidgetLeaTab() {
  const [categories, setCategories] = useState([]);
  const [checkup, setCheckup] = useState({ label: "Check-up complet", prix: 39, duree_minutes: 30, description: "", actif: true });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // category being edited (modal)
  const [editingCheckup, setEditingCheckup] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cats, chk] = await Promise.all([
        axios.get(`${API}/lea/categories`, { headers: authHeaders() }),
        axios.get(`${API}/lea/checkup`, { headers: authHeaders() }),
      ]);
      setCategories(cats.data.categories || []);
      setCheckup(chk.data || {});
    } catch (e) {
      toast.error("Erreur de chargement : " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = () => setEditing(emptyCategory());
  const handleEdit = (cat) => setEditing(JSON.parse(JSON.stringify(cat)));
  const handleClose = () => setEditing(null);

  const handleSave = async () => {
    try {
      const payload = { ...editing };
      // Nettoyage
      payload.subcategories = (payload.subcategories || []).filter((s) => s.label?.trim());
      if (!payload.label?.trim()) { toast.error("Le label est requis"); return; }
      if (!payload.chip_text?.trim()) { toast.error("Le message envoyé à Léa est requis"); return; }
      if (editing.id) {
        await axios.put(`${API}/lea/categories/${editing.id}`, payload, { headers: authHeaders() });
        toast.success("Catégorie mise à jour");
      } else {
        await axios.post(`${API}/lea/categories`, payload, { headers: authHeaders() });
        toast.success("Catégorie créée");
      }
      handleClose();
      await load();
    } catch (e) {
      toast.error("Erreur sauvegarde : " + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie "${cat.label}" ?`)) return;
    try {
      await axios.delete(`${API}/lea/categories/${cat.id}`, { headers: authHeaders() });
      toast.success("Catégorie supprimée");
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const handleToggleActif = async (cat) => {
    try {
      await axios.put(`${API}/lea/categories/${cat.id}`, { ...cat, actif: !cat.actif }, { headers: authHeaders() });
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const handleMoveOrder = async (cat, delta) => {
    try {
      await axios.put(`${API}/lea/categories/${cat.id}`, { ...cat, ordre: Math.max(0, cat.ordre + delta) }, { headers: authHeaders() });
      await load();
    } catch (e) {
      toast.error("Erreur : " + e.message);
    }
  };

  const handleCheckupSave = async () => {
    try {
      await axios.put(`${API}/lea/checkup`, checkup, { headers: authHeaders() });
      toast.success("Check-up mis à jour");
      setEditingCheckup(false);
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="text-sm text-gray-500" data-testid="widget-lea-loading">Chargement…</div>;

  return (
    <div className="space-y-4" data-testid="widget-lea-tab">
      {/* Header explicatif */}
      <div className="bg-gradient-to-br from-[#52a675]/5 to-transparent border border-[#52a675]/20 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Sparkles className="w-5 h-5 text-[#52a675] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-[#2d5f3f] mb-1">Catégories du widget Léa</div>
            <div className="text-sm text-gray-700">
              Configure les catégories affichées dans le widget public, leurs sous-options (avec prix et durée atelier), et le texte d'information.
              La <b>durée en minutes</b> de chaque sous-option est utilisée par l'agenda pour calculer la capacité journalière (14h/jour méca).
            </div>
          </div>
        </div>
      </div>

      {/* Check-up upsell config (singleton) */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="checkup-config-card">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-amber-900 mb-1">Upsell « Check-up »</div>
              {editingCheckup ? (
                <div className="space-y-2">
                  <input
                    type="text" placeholder="Label"
                    value={checkup.label || ""}
                    onChange={(e) => setCheckup({ ...checkup, label: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-amber-300 rounded"
                    data-testid="checkup-label"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number" step="0.01" min="0" placeholder="Prix CHF"
                      value={checkup.prix ?? 0}
                      onChange={(e) => setCheckup({ ...checkup, prix: parseFloat(e.target.value) || 0 })}
                      className="px-2 py-1 text-sm border border-amber-300 rounded"
                      data-testid="checkup-prix"
                    />
                    <input
                      type="number" min="5" max="600" placeholder="Durée min"
                      value={checkup.duree_minutes ?? 30}
                      onChange={(e) => setCheckup({ ...checkup, duree_minutes: parseInt(e.target.value) || 30 })}
                      className="px-2 py-1 text-sm border border-amber-300 rounded"
                      data-testid="checkup-duree"
                    />
                  </div>
                  <textarea
                    placeholder="Description (affichée dans le widget)"
                    value={checkup.description || ""}
                    onChange={(e) => setCheckup({ ...checkup, description: e.target.value })}
                    rows={2}
                    className="w-full px-2 py-1 text-sm border border-amber-300 rounded"
                    data-testid="checkup-description"
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox" checked={checkup.actif ?? true}
                      onChange={(e) => setCheckup({ ...checkup, actif: e.target.checked })}
                      data-testid="checkup-actif"
                    />
                    Actif
                  </label>
                  <div className="flex gap-2">
                    <button onClick={handleCheckupSave} className="px-3 py-1.5 bg-[#52a675] text-white rounded text-sm" data-testid="checkup-save">
                      <Save className="inline w-4 h-4 mr-1" /> Enregistrer
                    </button>
                    <button onClick={() => { setEditingCheckup(false); load(); }} className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm">
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-amber-900">
                  <div><b>{checkup.label}</b> — {fmtPrice(checkup.prix)} — {checkup.duree_minutes} min — {checkup.actif ? "✅ actif" : "❌ inactif"}</div>
                  <div className="text-xs text-amber-700 mt-1">{checkup.description}</div>
                </div>
              )}
            </div>
          </div>
          {!editingCheckup && (
            <button
              onClick={() => setEditingCheckup(true)}
              className="px-3 py-1 bg-white border border-amber-300 rounded text-sm text-amber-900 hover:bg-amber-100"
              data-testid="checkup-edit-btn"
            >Éditer</button>
          )}
        </div>
      </div>

      {/* Catégories */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Catégories ({categories.length})</h2>
        <button
          onClick={handleCreate}
          className="px-3 py-1.5 bg-[#52a675] text-white rounded text-sm font-medium hover:bg-[#3d8a5c] inline-flex items-center gap-1"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4" /> Ajouter une catégorie
        </button>
      </div>

      <div className="space-y-2">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className={`border rounded-lg p-3 ${cat.actif ? "bg-white border-gray-200" : "bg-gray-50 border-gray-200 opacity-60"}`}
            data-testid={`category-card-${cat.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => handleMoveOrder(cat, -1)} className="text-gray-400 hover:text-gray-700" data-testid={`order-up-${cat.id}`}><ChevronUp className="w-4 h-4" /></button>
                <span className="text-xs text-gray-500 text-center">{cat.ordre}</span>
                <button onClick={() => handleMoveOrder(cat, 1)} className="text-gray-400 hover:text-gray-700" data-testid={`order-down-${cat.id}`}><ChevronDown className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-gray-900">{cat.label}</span>
                  <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">{cat.icon}</span>
                  {cat.is_other && <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">"Autre" (envoi direct)</span>}
                  {cat.propose_checkup_upsell && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">+ check-up upsell</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{cat.subtitle}</div>
                <div className="text-xs text-gray-600 mt-1">
                  {cat.subcategories?.length ? (
                    <span>{cat.subcategories.length} sous-option(s) — durée atelier inline</span>
                  ) : (
                    <span>Pas de sous-option — durée par défaut : <b>{cat.duree_minutes_defaut} min</b></span>
                  )}
                </div>
                {cat.info_text && (
                  <div className="text-[11px] text-gray-500 mt-1 italic">ℹ️ {cat.info_text}</div>
                )}
                {cat.subcategories?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {cat.subcategories.map((sub) => (
                      <div key={sub.id} className="text-xs flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                        <span className={sub.actif === false ? "text-gray-400 line-through" : "text-gray-800"}>{sub.label}</span>
                        <span className="text-gray-600 font-mono">{fmtPrice(sub.prix)} · {sub.duree_minutes} min</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button onClick={() => handleEdit(cat)} className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50" data-testid={`edit-cat-${cat.id}`}>Éditer</button>
                <button onClick={() => handleToggleActif(cat)} className="px-2 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50" data-testid={`toggle-cat-${cat.id}`}>
                  {cat.actif ? "Désactiver" : "Activer"}
                </button>
                <button onClick={() => handleDelete(cat)} className="px-2 py-1 text-xs bg-white border border-red-300 text-red-700 rounded hover:bg-red-50" data-testid={`delete-cat-${cat.id}`}>
                  <Trash2 className="inline w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal d'édition */}
      {editing && <CategoryEditModal value={editing} onChange={setEditing} onSave={handleSave} onClose={handleClose} />}
    </div>
  );
}

function CategoryEditModal({ value, onChange, onSave, onClose }) {
  const cat = value;
  const updateField = (k, v) => onChange({ ...cat, [k]: v });

  const addSubcategory = () => {
    const subs = [...(cat.subcategories || []), { ...newSubcategory(), ordre: (cat.subcategories?.length || 0) + 1 }];
    updateField("subcategories", subs);
  };

  const updateSubcategory = (idx, k, v) => {
    const subs = [...cat.subcategories];
    subs[idx] = { ...subs[idx], [k]: v };
    updateField("subcategories", subs);
  };

  const deleteSubcategory = (idx) => {
    const subs = cat.subcategories.filter((_, i) => i !== idx);
    updateField("subcategories", subs);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="category-edit-modal">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="text-lg font-bold">{cat.id ? "Modifier" : "Nouvelle"} catégorie</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Champs principaux */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Label *</span>
              <input type="text" value={cat.label || ""} onChange={(e) => updateField("label", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" data-testid="modal-label" />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Sous-titre</span>
              <input type="text" value={cat.subtitle || ""} onChange={(e) => updateField("subtitle", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" data-testid="modal-subtitle" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Icône</span>
              <select value={cat.icon || "HelpCircle"} onChange={(e) => updateField("icon", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" data-testid="modal-icon">
                {ICON_CHOICES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Ordre (tri)</span>
              <input type="number" min="0" value={cat.ordre ?? 10} onChange={(e) => updateField("ordre", parseInt(e.target.value) || 0)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" data-testid="modal-ordre" />
            </label>
          </div>

          <label className="block text-sm">
            <span className="text-gray-700 font-medium">Message envoyé à Léa au clic *</span>
            <input type="text" value={cat.chip_text || ""} onChange={(e) => updateField("chip_text", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" placeholder="Bonjour, j'aimerais..." data-testid="modal-chip-text" />
          </label>

          <label className="block text-sm">
            <span className="text-gray-700 font-medium">Texte d'information (affiché après sélection)</span>
            <textarea value={cat.info_text || ""} onChange={(e) => updateField("info_text", e.target.value)} rows={2} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" placeholder='Ex: "Recyclage anciens pneus 2.50/pneu..."' data-testid="modal-info-text" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Durée par défaut (min)</span>
              <input type="number" min="5" max="600" value={cat.duree_minutes_defaut ?? 60} onChange={(e) => updateField("duree_minutes_defaut", parseInt(e.target.value) || 60)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded" data-testid="modal-duree-defaut" />
              <span className="text-xs text-gray-500">utilisée si pas de sous-cat / "Je ne sais pas"</span>
            </label>
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cat.actif ?? true} onChange={(e) => updateField("actif", e.target.checked)} data-testid="modal-actif" />
                <span>Catégorie active</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cat.is_other ?? false} onChange={(e) => updateField("is_other", e.target.checked)} data-testid="modal-is-other" />
                <span>"Autre" (envoi direct, sans flow)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cat.propose_checkup_upsell ?? false} onChange={(e) => updateField("propose_checkup_upsell", e.target.checked)} data-testid="modal-checkup-upsell" />
                <span>Propose check-up upsell</span>
              </label>
            </div>
          </div>

          {/* Sous-catégories */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-800">Sous-options (label + prix + durée atelier)</h4>
              <button onClick={addSubcategory} className="px-2 py-1 text-xs bg-[#52a675] text-white rounded hover:bg-[#3d8a5c]" data-testid="add-subcategory-btn">
                <Plus className="inline w-3 h-3 mr-0.5" /> Ajouter
              </button>
            </div>
            {(!cat.subcategories || cat.subcategories.length === 0) && (
              <div className="text-xs text-gray-500 italic flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Sans sous-option : le widget envoie directement le message à Léa (comme avant).
              </div>
            )}
            <div className="space-y-1.5">
              {(cat.subcategories || []).map((sub, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-1.5 items-center bg-gray-50 rounded px-2 py-1.5" data-testid={`subcat-row-${idx}`}>
                  <input
                    type="text" placeholder="Label" value={sub.label || ""}
                    onChange={(e) => updateSubcategory(idx, "label", e.target.value)}
                    className="col-span-6 px-2 py-1 text-sm border border-gray-300 rounded"
                    data-testid={`subcat-label-${idx}`}
                  />
                  <input
                    type="number" step="0.5" min="0" placeholder="CHF" value={sub.prix ?? 0}
                    onChange={(e) => updateSubcategory(idx, "prix", parseFloat(e.target.value) || 0)}
                    className="col-span-2 px-1 py-1 text-sm border border-gray-300 rounded text-right"
                    data-testid={`subcat-prix-${idx}`}
                  />
                  <input
                    type="number" min="5" max="600" placeholder="min" value={sub.duree_minutes ?? 60}
                    onChange={(e) => updateSubcategory(idx, "duree_minutes", parseInt(e.target.value) || 60)}
                    className="col-span-2 px-1 py-1 text-sm border border-gray-300 rounded text-right"
                    data-testid={`subcat-duree-${idx}`}
                    title="Durée atelier en minutes (utilisée par l'agenda)"
                  />
                  <input
                    type="number" min="0" placeholder="#" value={sub.ordre ?? 1}
                    onChange={(e) => updateSubcategory(idx, "ordre", parseInt(e.target.value) || 1)}
                    className="col-span-1 px-1 py-1 text-sm border border-gray-300 rounded text-center"
                    title="Ordre d'affichage"
                  />
                  <button onClick={() => deleteSubcategory(idx)} className="col-span-1 text-red-600 hover:bg-red-50 rounded p-1" data-testid={`subcat-delete-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">Colonnes : Label · Prix (CHF) · Durée atelier (min) · Ordre</div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50">Annuler</button>
          <button onClick={onSave} className="px-3 py-2 bg-[#52a675] text-white rounded text-sm font-semibold hover:bg-[#3d8a5c]" data-testid="modal-save">
            <Save className="inline w-4 h-4 mr-1" /> Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
